import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';

/**
 * Info tooltip for table headers that uses a Portal to render outside overflow
 * containers, so it reliably appears on top and is never clipped.
 */
export default function HeaderInfoTooltip({ label, tooltip }) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ left: 0, top: 0 });
  const wrapperRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  const show = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    setCoords({
      left: rect.left + rect.width / 2,
      top: rect.top
    });
    setVisible(true);
  }, []);

  const hide = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => setVisible(false), 100);
  }, []);

  const cancelHide = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    const onScroll = () => setVisible(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [visible]);

  return (
    <>
      <span
        ref={wrapperRef}
        className="nt-info-tooltip-wrapper"
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        {label}
        <span className="nt-info-icon">i</span>
      </span>
      {visible &&
        ReactDOM.createPortal(
          <span
            className="nt-info-tooltip nt-info-tooltip-portal"
            style={{
              left: coords.left,
              top: coords.top,
              transform: 'translateX(-50%) translateY(calc(-100% - 8px))'
            }}
            onMouseEnter={cancelHide}
            onMouseLeave={hide}
          >
            {tooltip}
          </span>,
          document.body
        )}
    </>
  );
}
